import React, { useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { makeStyles } from 'transfix-ui/core'
import { ITrailerStatesEnum } from 'types/graphqlTypes'
import { IShipmentProgressStopFragment } from '../../../graphql/ShipmentProgressStopFragment'
import { AddTrailerForm, IAddTrailerFormProps } from './AddTrailerForm'
import { TAddTrailerSchema } from './AddTrailerSchema'
import { useCarrierApplicationInfoQuery } from './graphql/CarrierApplicationInfo'
import {
  ICreateTrailerMutation,
  ICreateTrailerMutationVariables,
  useCreateTrailerMutation,
} from './graphql/CreateTrailerMutation'
import { useAddTrailerForm } from './useAddTrailerForm'

const useAddTrailerStyles = makeStyles(() => ({
  header: {
    paddingBottom: 0,
  },
  body: {
    textAlign: 'left',
  },
}))

export interface IAddTrailerProps extends Omit<IModalBaseProps, 'modalType'> {
  shipperApplicationId?: string
  carrierApplicationId?: string
  stop?: IShipmentProgressStopFragment
  showEmptyAtDelivery?: boolean
  showInactive?: boolean
  onSuccess?: (
    response: ICreateTrailerMutation['createTrailer'],
    mutationVariables: ICreateTrailerMutationVariables['input']
  ) => void
  /**
   * Name of a previously executed query that needs to be refetched after successfully adding a trailer
   */
  refetchQuery?: string
}

/**
 * When creating a trailer, send state to BE only if state has been manually set to inactive or unassigned.
 * BE will handle setting the state for the rest of the trailer states.
 */
const sendTrailerState = (status: TAddTrailerSchema['status']): boolean => {
  return ['inactive', 'unassigned'].includes(status || '')
}

export const AddTrailer = ({
  carrierApplicationId,
  onSuccess = () => {},
  onClose,
  open,
  shipperApplicationId,
  showEmptyAtDelivery,
  showInactive,
  stop,
  refetchQuery = 'ShipmentCarrierInfo',
  ...props
}: IAddTrailerProps) => {
  const styles = useAddTrailerStyles()
  const { mergedFacility, shipment } = stop || {}
  const { shipperFacility } = mergedFacility || {}

  const { carrierApplication, id: shipmentId, shipperApplication } = shipment || {}

  const { id: carrierId } = carrierApplication || {}

  const { data } = useCarrierApplicationInfoQuery({
    variables: {
      carrierId: carrierApplicationId || '',
    },
    skip: !carrierApplicationId,
  })
  const { carrierApplication: carrierApplicationProp } = data || {}

  const { openToast } = useGlobalToast()

  const methods = useAddTrailerForm({
    defaultValues: {
      carrier: carrierApplicationId || carrierId || '',
      shipment: shipmentId || '',
      facility: shipperFacility?.uuid || '',
      trailerNumber: '',
      trailerNotes: '',
      status: '',
      emptiedAt: {
        date: null,
        time: '',
      },
      arrivedAt: {
        date: null,
        time: '',
      },
    },
  })

  const [createTrailer] = useCreateTrailerMutation()

  const { handleSubmit, reset } = methods || {}

  const [mutationErrors, setMutationErrors] = useState<string | null>(null)

  const stateOptionsGenerator: IAddTrailerFormProps['displayStatusOptionsGenerator'] = v => v

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onClose()
        reset()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: handleSubmit(async (formData: TAddTrailerSchema) => {
        const {
          arrivedAt,
          carrier: carrierId,
          emptiedAt,
          facility: facilityUuid,
          shipment: shipmentId,
          status,
          trailerNotes: note,
          trailerNumber,
        } = formData || {}
        const shipperFacilityUuid = facilityUuid || null
        const facilityArrivedAtDate = arrivedAt.date
        const facilityArrivedAtTime = arrivedAt.time === '' ? null : arrivedAt.time
        const emptiedAtDate = emptiedAt.date
        const emptiedAtTime = emptiedAt.time === '' ? null : emptiedAt.time

        setMutationErrors(null)

        try {
          const inputVariables = {
            carrierId,
            number: trailerNumber,
            shipmentId,
            note,
            shipperFacilityUuid,
            facilityArrivedAtDate,
            facilityArrivedAtTime,
            emptiedAtDate,
            emptiedAtTime,
            ...(sendTrailerState(status) && { state: status as ITrailerStatesEnum }),
          }
          const { data, errors } = await createTrailer({
            variables: {
              input: inputVariables,
            },
            refetchQueries: [refetchQuery],
          })
          const { createTrailer: response } = data || {}
          const { errors: responseErrors } = response || {}
          if (errors && errors.length > 0) {
            setMutationErrors(errors.map(e => e.message).join('; '))
          } else if (responseErrors && responseErrors.length > 0) {
            setMutationErrors(responseErrors.map(e => e.message).join('; '))
          } else {
            onSuccess(response, inputVariables)
            openToast({
              toastType: 'default',
              toastMessage: 'Trailer successfully added.',
            })
            onClose()
            reset()
          }
        } catch (e) {
          setMutationErrors('There was an error adding this trailer')
        }
      }),
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]

  return (
    <ModalBase
      dataTest='AddTrailer'
      modalType={MODAL_TYPES.form}
      title='Add Trailer'
      size={4}
      open={open}
      actions={actions}
      headerClassName={styles.header}
      bodyClassName={styles.body}
      onClose={onClose}
      {...props}>
      <FormProvider {...methods}>
        <AddTrailerForm
          stop={stop}
          carrierApplication={carrierApplicationProp || undefined}
          mutationErrors={mutationErrors}
          shipperApplicationId={shipperApplicationId || shipperApplication?.id}
          showEmptyAtDelivery={showEmptyAtDelivery}
          displayStatusOptionsGenerator={showInactive ? stateOptionsGenerator : undefined}
        />
      </FormProvider>
    </ModalBase>
  )
}
